import React, {
  AnimationEvent,
  FunctionComponent,
  KeyboardEvent,
  useEffect,
  useState,
} from 'react';

import { alertIcon, successIcon } from '@justgiving/icons';
import SVG from '@justgiving/svg';
import useEventListener from '@use-it/event-listener';
import cn from 'classnames';

import { isEscapeKey } from '../../utils/keyboard';

import styles from './Toast.scss';

export type ToastProps = {
  message: string;
  type?: 'success' | 'warning';
  onDismiss?: () => void;
  dismissAfter?: number;
};

const typeToIcon = {
  success: successIcon,
  warning: alertIcon,
};

type ToastState = 'hide' | 'show' | 'showing' | 'hidden';

export const Toast: FunctionComponent<ToastProps> = ({
  message,
  type = 'success',
  onDismiss = () => {},
  dismissAfter,
}) => {
  const [state, setState] = useState<ToastState>('show');

  useEventListener('keydown', (event: KeyboardEvent) => {
    if (isEscapeKey(event)) {
      setState('hide');
      event.preventDefault();
    }
  });

  useEffect(() => {
    if (dismissAfter) {
      const timer = setTimeout(() => {
        setState('hide');
      }, dismissAfter);
      return () => clearTimeout(timer);
    }
  }, [dismissAfter]);

  const icon = typeToIcon[type];

  return (
    <div
      role="alert"
      className={cn(
        styles.toast,
        type === 'success' && styles.success,
        type === 'warning' && styles.warning,
        state === 'show' && styles.show,
        state === 'hide' && styles.hide,
        state === 'hidden' && styles.hidden,
      )}
      onClick={() => setState('hide')}
      onAnimationEnd={(evt: AnimationEvent<HTMLDivElement>) => {
        if (evt.animationName === styles.toastDown) {
          onDismiss();
          setState('hidden');
        }
      }}
    >
      <SVG markup={icon} className={styles.icon} />
      <div className={styles.message}>{message}</div>
    </div>
  );
};

export default Toast;
